'use client'

import { useMemo } from 'react'
import { cn } from '@/lib/tailwind'

/**
 * Daily journal prompts — ADHD-friendly nudges.
 * One prompt per app session, no repeats within a day.
 */
export const JOURNAL_PROMPTS = [
  "What's one thing you noticed today?",
  'What took more energy than expected?',
  "What's something you finished recently?",
  'Who made your day better today?',
  "What's been on your mind this week?",
  'What did you learn this week?',
  "What's something you're looking forward to?",
  'What felt easier than expected today?',
  'What would you tell a friend about your week?',
  "What's one thing you want to remember about today?",
] as const

interface PromptSeen {
  /** Today's midnight timestamp */
  d: number
  /** Indices of prompts already shown today */
  seen: number[]
}

const STORAGE_KEY = 'prompt-seen'

function getTodayMidnight(): number {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
}

/**
 * Pick a random prompt that hasn't been shown yet today.
 * Tracks seen prompts in sessionStorage with a single self-cleaning key.
 */
export function getDailyPrompt(): string {
  const todayMidnight = getTodayMidnight()

  let state: PromptSeen = { d: todayMidnight, seen: [] }

  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as PromptSeen
      if (parsed.d === todayMidnight) {
        state = parsed
      }
    }
  } catch {
    // Corrupted storage — start fresh
  }

  // If all prompts seen, reset
  if (state.seen.length >= JOURNAL_PROMPTS.length) {
    state.seen = []
  }

  // Pick a random unseen index
  const available = Array.from(
    { length: JOURNAL_PROMPTS.length },
    (_, i) => i,
  ).filter((i) => !state.seen.includes(i))
  const randomIndex = available[Math.floor(Math.random() * available.length)]!

  // Persist
  state.seen.push(randomIndex)
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ d: todayMidnight, seen: state.seen }),
    )
  } catch {
    // sessionStorage full or unavailable — continue without persisting
  }

  return JOURNAL_PROMPTS[randomIndex]!
}

interface AnimatedPlaceholderProps {
  /** Whether the placeholder is visible (no content in textarea) */
  show: boolean
  className?: string
}

/**
 * Animated shimmer placeholder — renders a daily prompt in Instrument Serif italic
 * with a metallic gradient sweep in petrol tones.
 *
 * Positioned absolutely inside the textarea container.
 * Uses `pointer-events-none` so clicks pass through to the textarea.
 */
export function AnimatedPlaceholder({
  show,
  className,
}: AnimatedPlaceholderProps) {
  const prompt = useMemo(() => getDailyPrompt(), [])

  return (
    <span
      data-testid="animated-placeholder"
      aria-hidden="true"
      className={cn(
        'shimmer-text pointer-events-none absolute top-0 left-0 font-[family-name:var(--font-instrument-serif)] text-lg italic transition-opacity duration-200 sm:text-base',
        show ? 'opacity-100' : 'opacity-0',
        className,
      )}
    >
      {prompt}
    </span>
  )
}
